import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Slash, TrendingUp, BarChart3, Phone, FileSearch, Target, Calendar, Globe, X } from 'lucide-react';
import { useAppearance } from '../contexts/AppearanceContext';

const SLASH_COMMANDS = [
  {
    id: 'rank',
    name: '/rank',
    description: 'Check SEO ranking positions',
    icon: TrendingUp,
    fields: [
      { name: 'domain', label: 'Domain Name', type: 'text', required: true, placeholder: 'example.com' },
      { name: 'date_from', label: 'From Date', type: 'date', required: false },
      { name: 'date_to', label: 'To Date', type: 'date', required: false }
    ]
  },
  {
    id: 'ads',
    name: '/ads',
    description: 'Analyze advertisement data',
    icon: Target,
    fields: [
      { name: 'domain', label: 'Domain Name', type: 'text', required: true, placeholder: 'example.com' },
      { name: 'date_from', label: 'From Date', type: 'date', required: false },
      { name: 'date_to', label: 'To Date', type: 'date', required: false }
    ]
  },
  {
    id: 'analytics',
    name: '/analytics',
    description: 'View website analytics',
    icon: BarChart3,
    fields: [
      { name: 'domain', label: 'Domain Name', type: 'text', required: true, placeholder: 'example.com' },
      { name: 'metric', label: 'Metric', type: 'select', required: false, options: ['traffic', 'conversions', 'bounce_rate'] }
    ]
  },
  {
    id: 'calls',
    name: '/calls',
    description: 'Call tracking data',
    icon: Phone,
    fields: [
      { name: 'phone_number', label: 'Phone Number', type: 'text', required: true, placeholder: '+1234567890' },
      { name: 'date_from', label: 'From Date', type: 'date', required: false }
    ]
  },
  {
    id: 'audit',
    name: '/audit',
    description: 'SEO audit report',
    icon: FileSearch,
    fields: [
      { name: 'domain', label: 'Domain Name', type: 'text', required: true, placeholder: 'example.com' },
      { name: 'audit_type', label: 'Audit Type', type: 'select', required: false, options: ['technical', 'content', 'backlinks', 'full'] }
    ]
  }
];

const SlashCommands = ({ 
  message, 
  onSlashCommand, 
  onMessageChange, 
  showCommands, 
  onToggleCommands,
  isVisible 
}) => {
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [formData, setFormData] = useState({});
  const [filteredCommands, setFilteredCommands] = useState(SLASH_COMMANDS);
  const { isSuppleMode, appearanceMode } = useAppearance();
  const commandsRef = useRef(null);

  useEffect(() => {
    if (message.startsWith('/')) {
      const query = message.slice(1).toLowerCase();
      const filtered = SLASH_COMMANDS.filter(cmd => 
        cmd.name.slice(1).toLowerCase().includes(query) ||
        cmd.description.toLowerCase().includes(query)
      );
      setFilteredCommands(filtered);
    } else {
      setFilteredCommands(SLASH_COMMANDS);
    }
  }, [message]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (commandsRef.current && !commandsRef.current.contains(event.target)) {
        setSelectedCommand(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCommandSelect = (command) => {
    setSelectedCommand(command);
    setFormData({});
    onMessageChange(command.name + ' ');
  };

  const handleFormChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleExecuteCommand = () => {
    const requiredFields = selectedCommand.fields.filter(f => f.required);
    const missingFields = requiredFields.filter(f => !formData[f.name]);
    
    if (missingFields.length > 0) {
      alert(`Please fill in required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    onSlashCommand(selectedCommand.id, formData);
    setSelectedCommand(null);
    setFormData({});
    onMessageChange('');
  };

  const handleCancel = () => {
    setSelectedCommand(null);
    setFormData({});
    onMessageChange('');
  };

  // Command form modal
  if (selectedCommand) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-start justify-center bg-black bg-opacity-50 pt-20" ref={commandsRef}>
        <div className={`w-full max-w-md mx-4 rounded-lg p-6 ${
          isSuppleMode 
            ? 'supple-card-glass supple-border-gradient-blue backdrop-blur-md' 
            : 'bg-white border border-gray-200'
        }`}>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isSuppleMode 
                ? 'supple-btn-gradient-blue' 
                : 'bg-blue-500'
            }`}>
              <selectedCommand.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className={`font-semibold ${
                isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
              }`}>{selectedCommand.name}</h3>
              <p className={`text-sm ${
                isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
              }`}>{selectedCommand.description}</p>
            </div>
          </div>

          <div className="space-y-4">
            {selectedCommand.fields.map(field => (
              <div key={field.name}>
                <label className={`block text-sm font-medium mb-1 ${
                  isSuppleMode ? 'supple-text-primary' : 'text-gray-700'
                }`}>
                  {field.label} {field.required && <span className="text-red-500">*</span>}
                </label>
                {field.type === 'select' ? (
                  <select
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFormChange(field.name, e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 ${
                      isSuppleMode
                        ? 'supple-card-glass supple-border-subtle focus:supple-border-gradient-blue supple-text-primary'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  >
                    <option value="">Select {field.label}</option>
                    {field.options?.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type}
                    value={formData[field.name] || ''}
                    onChange={(e) => handleFormChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 ${
                      isSuppleMode
                        ? 'supple-card-glass supple-border-subtle focus:supple-border-gradient-blue supple-text-primary placeholder-supple-text-muted'
                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleExecuteCommand}
              className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                isSuppleMode
                  ? 'supple-btn-gradient-blue hover:supple-btn-gradient-blue-hover supple-text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Execute Command
            </button>
            <button
              onClick={handleCancel}
              className={`px-4 py-2 rounded-lg transition-colors ${
                isSuppleMode
                  ? 'supple-card-glass supple-text-muted hover:supple-card-glass-hover'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Command dropdown or modal
  if (isVisible && showCommands) {
    // If message is empty, render as modal (clicked from button)
    if (!message || message === "") {
      return createPortal(
        <div 
          className="fixed inset-0 z-[9999] flex items-start justify-center bg-black bg-opacity-50 pt-20"
          onClick={onToggleCommands}
        >
          <div 
            ref={commandsRef}
            className={`w-full max-w-md mx-4 rounded-lg border shadow-lg ${
              isSuppleMode 
                ? 'supple-card-glass supple-border-gradient-blue backdrop-blur-md' 
                : 'bg-white border-gray-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`p-4 border-b flex items-center justify-between ${
              isSuppleMode ? 'border-gray-200' : 'border-gray-200'
            }`}>
              <h3 className={`font-medium ${
                isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
              }`}>Slash Commands</h3>
              <button
                onClick={onToggleCommands}
                className={`text-gray-400 hover:text-gray-600 transition-colors p-1`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {filteredCommands.map(command => {
                const Icon = command.icon;
                return (
                  <button
                    key={command.id}
                    onClick={() => handleCommandSelect(command)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                      isSuppleMode 
                        ? 'hover:bg-gray-100' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isSuppleMode 
                        ? 'supple-btn-gradient-blue' 
                        : 'bg-blue-500'
                    }`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${
                        isSuppleMode ? 'supple-text-primary' : 'text-gray-900'
                      }`}>{command.name}</p>
                      <p className={`text-sm ${
                        isSuppleMode ? 'supple-text-muted' : 'text-gray-600'
                      }`}>{command.description}</p>
                    </div>
                  </button>
                );
              })}
              {filteredCommands.length === 0 && (
                <div className={`p-4 text-center text-sm ${
                  isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
                }`}>
                  No commands found
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      );
    }
    
    // Otherwise render as dropdown (typing in input)
    return (
      <div 
        ref={commandsRef}
        className={`absolute bottom-full left-0 mb-2 w-full max-w-md rounded-lg border shadow-lg z-[1000] ${
          isSuppleMode 
            ? 'bg-white/95 backdrop-blur-md border-gray-200 supple-shadow-lg' 
            : 'bg-white border-gray-200'
        }`}
      >
        <div className={`p-3 border-b ${
          isSuppleMode ? 'border-gray-200' : 'border-gray-200'
        }`}>
          <h3 className={`font-medium text-sm ${
            isSuppleMode ? 'text-gray-900' : 'text-gray-900'
          }`}>Available Commands</h3>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filteredCommands.map(command => {
            const Icon = command.icon;
            return (
              <button
                key={command.id}
                onClick={() => handleCommandSelect(command)}
                className={`w-full text-left px-3 py-2 flex items-center gap-3 transition-colors ${
                  isSuppleMode 
                    ? 'hover:bg-gray-100' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  isSuppleMode 
                    ? 'supple-btn-gradient-blue' 
                    : 'bg-blue-500'
                }`}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <p className={`font-medium text-sm ${
                    isSuppleMode ? 'text-gray-900' : 'text-gray-900'
                  }`}>{command.name}</p>
                  <p className={`text-xs ${
                    isSuppleMode ? 'text-gray-600' : 'text-gray-500'
                  }`}>{command.description}</p>
                </div>
              </button>
            );
          })}
          {filteredCommands.length === 0 && (
            <div className={`p-3 text-center text-sm ${
              isSuppleMode ? 'supple-text-muted' : 'text-gray-500'
            }`}>
              No commands found
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default SlashCommands;